// DashboardLayout.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import './dashboard.css';
import Navbar from '../../shared/navbar.js';
import SearchBar from '../../shared/SearchBar.js';
import { useNavigate } from 'react-router-dom';
import GridLayout, { WidthProvider } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  WIDGET_REGISTRY,
  WIDGET_CATEGORIES,
  buildDefaultItems,
  itemsToLayout,
  mergeLayoutIntoItems,
  getDefaultLayoutItem,
  getNextY,
} from './widgetRegistry';

const DASHBOARD_LAYOUT_KEY = 'rentafai_dashboard_layout';
const STICKY_NOTES_KEY = 'rentafai_sticky_notes';
const DEFAULT_LAYOUT_NAME = 'Default';

const GridLayoutWithWidth = WidthProvider(GridLayout);

function loadDashboardLayout() {
  try {
    const s = localStorage.getItem(DASHBOARD_LAYOUT_KEY);
    if (s) {
      const parsed = JSON.parse(s);
      if (parsed.layoutName && parsed.layouts && Array.isArray(parsed.layouts[parsed.layoutName])) {
        return { layoutName: parsed.layoutName, layouts: parsed.layouts, items: parsed.layouts[parsed.layoutName] };
      }
    }
  } catch (e) {}
  const defaultItems = buildDefaultItems();
  return { layoutName: DEFAULT_LAYOUT_NAME, layouts: { [DEFAULT_LAYOUT_NAME]: defaultItems }, items: defaultItems };
}

function saveDashboardLayout(layoutName, layouts) {
  try { localStorage.setItem(DASHBOARD_LAYOUT_KEY, JSON.stringify({ layoutName, layouts })); } catch (e) {}
}

function loadStickyNotes() {
  try { const s = localStorage.getItem(STICKY_NOTES_KEY); return s ? JSON.parse(s) : {}; } catch (e) { return {}; }
}
function saveStickyNotes(notes) {
  try { localStorage.setItem(STICKY_NOTES_KEY, JSON.stringify(notes)); } catch (e) {}
}

/* ── Business logic helpers ───────────────── */
const calculateActiveApplications = (applicantsData) => {
  const now = new Date();
  const activeStatuses = {
    'new': { active: true, maxDays: 7, priority: 'high' },
    'qualified': { active: true, maxDays: 14, priority: 'high' },
    'contacted': { active: true, maxDays: 10, priority: 'medium' },
    'viewing': { active: true, maxDays: 21, priority: 'high' },
    'application': { active: true, maxDays: 14, priority: 'high' },
    'background': { active: true, maxDays: 30, priority: 'medium' },
    'screening': { active: true, maxDays: 21, priority: 'medium' },
    'review': { active: true, maxDays: 14, priority: 'high' },
    'proposal': { active: true, maxDays: 14, priority: 'high' },
    'negotiation': { active: true, maxDays: 21, priority: 'high' },
    'approved': { active: true, maxDays: 30, priority: 'high' },
    'lease_signed': { active: false }, 'moved_in': { active: false },
    'closed_won': { active: false }, 'closed_lost': { active: false }, 'rejected': { active: false },
  };
  return applicantsData.filter(a => {
    const cfg = activeStatuses[a.status || 'new'];
    if (!cfg || !cfg.active) return false;
    const days = Math.floor((now - new Date(a.created_at || a.last_contact)) / 86400000);
    return days <= cfg.maxDays || cfg.priority === 'high';
  }).length;
};

const daysActive = (d) => d ? Math.floor((new Date() - new Date(d)) / 86400000) : 0;

const appPriority = (a) => {
  const s = a.status || 'new';
  if (['new','viewing','application','proposal','negotiation','approved'].includes(s)) return 'high';
  if ((a.lead_score || 0) >= 80) return 'high';
  if ((a.lead_score || 0) >= 60) return 'medium';
  if (daysActive(a.created_at) > 30) return 'high';
  if (daysActive(a.created_at) > 14) return 'medium';
  return 'low';
};

const getHealthMetrics = (data) => {
  const m = { total: data.length, active: 0, stale: 0, highPriority: 0, needsFollowUp: 0, conversionReady: 0 };
  const closed = ['closed_won','closed_lost','rejected','lease_signed','moved_in'];
  const activeList = ['new','qualified','contacted','viewing','application','background','screening','review','proposal','negotiation','approved'];
  data.forEach(a => {
    const s = a.status || 'new';
    if (activeList.includes(s)) m.active++;
    if (daysActive(a.created_at) > 45 && !closed.includes(s)) m.stale++;
    if (appPriority(a) === 'high') m.highPriority++;
    if (daysActive(a.last_contact) > 7 && !closed.includes(s)) m.needsFollowUp++;
    if (s === 'approved') m.conversionReady++;
  });
  return m;
};

/* ── Component ───────────────────────────── */
export default function DashboardLayout() {
  const [username, setUsername] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [layoutState, setLayoutState] = useState(() => loadDashboardLayout());
  const { layoutName, layouts, items } = layoutState;
  const setItems = (items) => setLayoutState(s => ({ ...s, items }));
  const setLayouts = (layouts) => setLayoutState(s => ({ ...s, layouts }));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [layoutSelectOpen, setLayoutSelectOpen] = useState(false);
  const layoutSelectRef = useRef(null);
  const [stickyNotes, setStickyNotes] = useState(() => loadStickyNotes());

  const [dashboardData, setDashboardData] = useState({
    activeApplications: 0, activeLeases: 0, properties: 0, monthlyRevenue: 0,
    recentApplications: [], upcomingTasks: [], applicationMetrics: null,
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Close layout dropdown on outside click
  useEffect(() => {
    if (!layoutSelectOpen) return;
    const h = (e) => { if (layoutSelectRef.current && !layoutSelectRef.current.contains(e.target)) setLayoutSelectOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [layoutSelectOpen]);

  useEffect(() => {
    const u = localStorage.getItem('username');
    if (u) setUsername(u);
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [propRes, tenRes, mainRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/properties`),
        fetch(`${API_BASE_URL}/api/tenants`),
        fetch(`${API_BASE_URL}/api/maintenance-requests`),
      ]);
      const propertiesData = await propRes.json();
      const tenantsData = await tenRes.json();
      const maintenanceData = await mainRes.json();

      const activeLeases = tenantsData.filter(t => t['Lease Status'] === 'Active' || t['Lease Status'] === 'Current' || (!t['Lease Status'] && t['First Name'])).length;
      const monthlyRevenue = tenantsData.reduce((s, t) => s + parseFloat(t['Monthly Rent'] || t['Rent Amount'] || 0), 0);

      let applicantsData = [];
      let activeApplicationsCount = 0;
      try {
        const r = await fetch(`${API_BASE_URL}/api/applicants`);
        if (r.ok) { applicantsData = await r.json(); activeApplicationsCount = calculateActiveApplications(applicantsData); }
      } catch (e) { console.error('Error loading applicants:', e); }

      const recentApplications = applicantsData
        .sort((a, b) => new Date(b.created_at || b.last_contact) - new Date(a.created_at || a.last_contact))
        .slice(0, 5)
        .map(a => ({
          id: a.id,
          name: `${a.first_name || ''} ${a.last_name || ''}`.trim() || 'Unknown',
          property: a.property_interest || 'Not Specified',
          date: a.last_contact || a.created_at || new Date().toISOString().split('T')[0],
          status: a.status || 'new',
          leadScore: a.lead_score || 0,
          source: a.source || 'Unknown',
          daysActive: daysActive(a.created_at),
          priority: appPriority(a),
        }));

      const upcomingTasks = [];
      maintenanceData.slice(0, 2).forEach((r, i) => {
        upcomingTasks.push({ id: `maint-${r.id}`, type: 'maintenance', title: `Review: ${r.request_type}`, dueDate: i === 0 ? 'Today' : 'Tomorrow', priority: r.priority || 'medium', icon: '🔧' });
      });
      upcomingTasks.push({ id: 'lease-renewal', type: 'lease', title: 'Process lease renewal for Unit 301', dueDate: 'This week', priority: 'high', icon: '📋' });

      setDashboardData({
        activeApplications: activeApplicationsCount, activeLeases, properties: propertiesData.length,
        monthlyRevenue, recentApplications, upcomingTasks, applicationMetrics: getHealthMetrics(applicantsData),
      });
    } catch (e) { console.error('Error loading dashboard data:', e); } finally { setLoading(false); }
  };

  const handleCardClick = (t) => { if (t === 'revenue') navigate('/financial'); else navigate('/databases'); };
  const handleViewAll = (s) => { if (s === 'tasks') navigate('/maintenance-queue'); else navigate('/databases'); };
  const handleApplicationClick = (a) => navigate(`/tenant-edit/${a.id}`);
  const handleTaskClick = (t) => { if (t.type === 'maintenance') navigate('/maintenance-queue'); else navigate('/databases'); };

  const currentItemIds = useMemo(() => items.map(it => it.i), [items]);

  const addWidget = (id) => {
    if (!WIDGET_REGISTRY[id] || currentItemIds.includes(id)) return;
    const newItem = getDefaultLayoutItem(id, getNextY(items));
    if (!newItem) return;
    const nextItems = [...items, newItem];
    setItems(nextItems);
    const nextLayouts = { ...layouts, [layoutName]: nextItems };
    setLayouts(nextLayouts);
    saveDashboardLayout(layoutName, nextLayouts);
    setPickerOpen(false); setPickerSearch('');
  };

  const removeWidget = (id) => {
    const nextItems = items.filter(it => it.i !== id);
    setItems(nextItems);
    const nextLayouts = { ...layouts, [layoutName]: nextItems };
    setLayouts(nextLayouts);
    saveDashboardLayout(layoutName, nextLayouts);
  };

  const onLayoutChange = useCallback((newLayout) => {
    const merged = mergeLayoutIntoItems(items, newLayout);
    setItems(merged);
    const nextLayouts = { ...layouts, [layoutName]: merged };
    setLayouts(nextLayouts);
    saveDashboardLayout(layoutName, nextLayouts);
  }, [items, layouts, layoutName]);

  const switchLayout = (name) => {
    if (!layouts[name]) return;
    setLayoutState(s => ({ ...s, layoutName: name, items: s.layouts[name] || s.items }));
    setLayoutSelectOpen(false);
    saveDashboardLayout(name, layouts);
  };

  const saveCurrentAs = (name) => {
    const t = name && name.trim();
    if (!t) return;
    const nextLayouts = { ...layouts, [t]: items };
    setLayoutState(s => ({ ...s, layoutName: t, layouts: nextLayouts }));
    setLayoutSelectOpen(false);
    saveDashboardLayout(t, nextLayouts);
  };

  const availableToAdd = useMemo(() => {
    return Object.entries(WIDGET_REGISTRY)
      .filter(([id, def]) => def.implemented !== false && !currentItemIds.includes(id))
      .map(([id, def]) => ({ id, ...def }));
  }, [currentItemIds]);

  // Sticky note helpers
  const updateStickyNote = (id, text) => {
    const next = { ...stickyNotes, [id]: text };
    setStickyNotes(next);
    saveStickyNotes(next);
  };

  const scoreClass = (s) => s >= 80 ? '' : s >= 60 ? 'mid' : 'low';

  /* ── Render a single widget by id ── */
  const renderWidget = (id) => {
    const def = WIDGET_REGISTRY[id];
    if (!def) return null;

    // Sticky notes
    if (def.type === 'sticky') {
      return (
        <div className="dash-sticky" style={{ background: def.color || '#fef9c3' }}>
          <div className="dash-sticky-head">
            <span className="dash-sticky-title">{def.label}</span>
          </div>
          <textarea
            className="dash-sticky-text"
            placeholder="Type a note..."
            value={stickyNotes[id] || ''}
            onChange={(e) => updateStickyNote(id, e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
          />
        </div>
      );
    }

    if (id === 'search') return <div className="dash-search-wrap"><SearchBar /></div>;

    if (id === 'quick-actions') {
      return (
        <div className="dash-quick-actions">
          <button className="dash-quick-action" onClick={() => navigate('/databases')}><span className="dash-quick-action-icon">+</span> Add Tenant</button>
          <button className="dash-quick-action" onClick={() => navigate('/properties/new-wizard')}><span className="dash-quick-action-icon">🏠</span> Add Property</button>
          <button className="dash-quick-action" onClick={() => navigate('/maintenance')}><span className="dash-quick-action-icon">🔧</span> New Request</button>
          <button className="dash-quick-action" onClick={() => navigate('/financial')}><span className="dash-quick-action-icon">💳</span> Record Payment</button>
          <button className="dash-quick-action" onClick={() => navigate('/pricing-engine')}><span className="dash-quick-action-icon">📊</span> Price Analysis</button>
          <button className="dash-quick-action" onClick={() => navigate('/legal')}><span className="dash-quick-action-icon">⚖️</span> Legal Review</button>
        </div>
      );
    }

    // KPI Cards
    if (def.type === 'card') {
      return (
        <div className="dash-kpi" onClick={() => handleCardClick(id)}>
          <div className="dash-kpi-top">
            <div className={`dash-kpi-icon ${id === 'applications' ? 'apps' : id === 'leases' ? 'leases' : id === 'properties' ? 'props' : 'rev'}`}>{def.icon}</div>
            <span className="dash-kpi-label">{def.label}</span>
          </div>
          {id === 'applications' && (
            <>
              <div className="dash-kpi-value">{dashboardData.activeApplications}</div>
              {dashboardData.applicationMetrics && (
                <div className="dash-kpi-metrics">
                  <div className="dash-kpi-metric"><span>High Priority</span><span>{dashboardData.applicationMetrics.highPriority}</span></div>
                  <div className="dash-kpi-metric"><span>Need Follow-up</span><span>{dashboardData.applicationMetrics.needsFollowUp}</span></div>
                </div>
              )}
            </>
          )}
          {id === 'leases' && <><div className="dash-kpi-value">{dashboardData.activeLeases}</div><div className="dash-kpi-sub">Active tenants</div></>}
          {id === 'properties' && <><div className="dash-kpi-value">{dashboardData.properties}</div><div className="dash-kpi-sub">Managed units</div></>}
          {id === 'revenue' && <><div className="dash-kpi-value">${dashboardData.monthlyRevenue.toLocaleString()}</div><div className="dash-kpi-sub">Projected</div></>}
        </div>
      );
    }

    // Recent Applications panel
    if (id === 'recent-applications') {
      return (
        <div className="dash-panel">
          <div className="dash-panel-head"><h2 className="dash-panel-title">Recent Applications</h2><button className="dash-panel-action" onClick={() => handleViewAll('applications')}>View All</button></div>
          <div className="dash-panel-body">
            <table className="dash-app-table">
              <thead><tr><th>Applicant</th><th>Score</th><th>Status</th><th>Days</th></tr></thead>
              <tbody>
                {dashboardData.recentApplications.map(app => (
                  <tr key={app.id} onClick={() => handleApplicationClick(app)}>
                    <td><div className="dash-app-name">{app.name}</div><div className="dash-app-prop">{app.property}</div></td>
                    <td><span className={`dash-app-score ${scoreClass(app.leadScore)}`}>{app.leadScore}</span></td>
                    <td><div className="dash-badges"><span className={`dash-badge status-${app.status}`}>{app.status}</span><span className={`dash-badge pri-${app.priority}`}>{app.priority}</span></div></td>
                    <td><span className="dash-app-meta">{app.daysActive}d</span></td>
                  </tr>
                ))}
                {dashboardData.recentApplications.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: '#9ca3af', padding: 24 }}>No recent applications</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Upcoming Tasks panel
    if (id === 'upcoming-tasks') {
      return (
        <div className="dash-panel">
          <div className="dash-panel-head"><h2 className="dash-panel-title">Upcoming Tasks</h2><button className="dash-panel-action" onClick={() => handleViewAll('tasks')}>View All</button></div>
          <div className="dash-panel-body">
            {dashboardData.upcomingTasks.map(task => (
              <div key={task.id} className="dash-task" onClick={() => handleTaskClick(task)}>
                <div className="dash-task-icon">{task.icon}</div>
                <div className="dash-task-info"><div className="dash-task-title">{task.title}</div><div className="dash-task-due">{task.dueDate}</div></div>
                <div className="dash-task-pri"><span className={`dash-badge pri-${task.priority}`}>{task.priority}</span></div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Databases panel
    if (id === 'databases') {
      return (
        <div className="dash-panel">
          <div className="dash-panel-head"><h2 className="dash-panel-title">Databases</h2><button className="dash-panel-action" onClick={() => navigate('/databases')}>Open</button></div>
          <div className="dash-panel-body">
            {[{ icon: '🏠', name: 'Properties' }, { icon: '👥', name: 'Tenants' }, { icon: '📋', name: 'Applicants' }, { icon: '🧑‍💼', name: 'Staff' }].map(db => (
              <div key={db.name} className="dash-db-link" onClick={() => navigate('/databases')}>
                <span className="dash-db-link-icon">{db.icon}</span><span className="dash-db-link-name">{db.name}</span><span className="dash-db-link-arrow">&#8250;</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Legal Cases panel
    if (id === 'legal-cases') {
      return (
        <div className="dash-panel">
          <div className="dash-panel-head"><h2 className="dash-panel-title">Legal Cases</h2><button className="dash-panel-action" onClick={() => navigate('/legal')}>Open</button></div>
          <div className="dash-panel-body"><div className="dash-panel-hint">View legal documents and cases.<br /><button className="dash-panel-hint-btn" onClick={() => navigate('/legal')}>Go to Legal Dashboard</button></div></div>
        </div>
      );
    }

    // Move-in/out panel
    if (id === 'move-in-out') {
      return (
        <div className="dash-panel">
          <div className="dash-panel-head"><h2 className="dash-panel-title">Move-ins / Move-outs</h2><button className="dash-panel-action" onClick={() => navigate('/databases')}>View Tenants</button></div>
          <div className="dash-panel-body"><div className="dash-panel-hint">Manage upcoming move-ins and move-outs.<br /><button className="dash-panel-hint-btn" onClick={() => navigate('/databases')}>Go to Tenants</button></div></div>
        </div>
      );
    }

    return <div className="dash-panel"><div className="dash-panel-head"><h2 className="dash-panel-title">{def.label}</h2></div><div className="dash-panel-body"><div className="dash-panel-hint">Coming soon</div></div></div>;
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="dash">
        <Navbar selected="Dashboard" />
        <main className="dash-main">
          <div className="dash-loading"><div className="spinner"></div><p>Loading dashboard...</p></div>
        </main>
      </div>
    );
  }

  /* ── Main Render ── */
  return (
    <div className="dash">
      <Navbar selected="Dashboard" />

      <main className="dash-main">
        {/* Top Bar */}
        <div className="dash-top-bar">
          <div>
            <h1 className="dash-title">Dashboard</h1>
            <p className="dash-subtitle">Welcome back, {username || 'User'}. Here's what's happening.</p>
          </div>
          <div className="dash-customize-bar">
            <div className="dashboard-layout-select" ref={layoutSelectRef}>
              <button type="button" className="dashboard-layout-btn" onClick={() => setLayoutSelectOpen(!layoutSelectOpen)}>
                Layout: {layoutName} &#9660;
              </button>
              {layoutSelectOpen && (
                <div className="dashboard-layout-dropdown">
                  {Object.keys(layouts).map(name => (
                    <button key={name} type="button" className={name === layoutName ? 'active' : ''} onClick={() => switchLayout(name)}>{name}</button>
                  ))}
                  <div className="dashboard-layout-save">
                    <input type="text" placeholder="Save as..." id="save-layout-name" onKeyDown={e => e.key === 'Enter' && saveCurrentAs(e.target.value)} />
                    <button type="button" onClick={() => saveCurrentAs(document.getElementById('save-layout-name')?.value)}>Save</button>
                  </div>
                </div>
              )}
            </div>
            <button className="dashboard-customize-btn" onClick={() => setEditMode(!editMode)}>
              {editMode ? 'Done' : 'Customize'}
            </button>
            {editMode && <button type="button" className="dashboard-add-widget-btn" onClick={() => setPickerOpen(true)}>+ Add widget</button>}
          </div>
        </div>

        {/* Drag/drop grid canvas */}
        <div className="dash-canvas">
          {items.length > 0 ? (
            <GridLayoutWithWidth
              className="dash-grid"
              layout={itemsToLayout(items)}
              onLayoutChange={onLayoutChange}
              cols={12}
              rowHeight={60}
              margin={[16, 16]}
              isDraggable={editMode}
              isResizable={editMode}
              draggableHandle=".dash-widget-handle"
              compactType={null}
              preventCollision={false}
            >
              {items.map(item => {
                const id = item.i;
                const def = WIDGET_REGISTRY[id];
                if (!def) return null;
                return (
                  <div key={id} className={`dash-widget ${editMode ? 'dash-widget-edit' : ''}`}>
                    {editMode && (
                      <div className="dash-widget-toolbar">
                        <span className="dash-widget-handle" title="Drag">⋮⋮</span>
                        <button type="button" className="dash-widget-remove" onClick={() => removeWidget(id)} title="Remove">&times;</button>
                      </div>
                    )}
                    <div className="dash-widget-content">
                      {renderWidget(id)}
                    </div>
                  </div>
                );
              })}
            </GridLayoutWithWidth>
          ) : (
            <div className="dash-empty">
              <p>No widgets yet. Click <strong>Customize</strong> then <strong>+ Add widget</strong> to get started.</p>
            </div>
          )}
        </div>

        {/* Widget picker drawer */}
        {editMode && pickerOpen && (
          <div className="dashboard-picker-overlay" onClick={() => setPickerOpen(false)}>
            <div className="dashboard-picker-drawer" onClick={e => e.stopPropagation()}>
              <div className="dashboard-picker-header">
                <h3>Add widget</h3>
                <button type="button" className="dashboard-picker-close" onClick={() => setPickerOpen(false)}>&times;</button>
              </div>
              <input type="text" className="dashboard-picker-search" placeholder="Search widgets..." value={pickerSearch} onChange={e => setPickerSearch(e.target.value)} />
              <div className="dashboard-picker-list">
                {Object.entries(WIDGET_CATEGORIES).map(([catId, cat]) => {
                  const catWidgets = availableToAdd.filter(w => w.category === catId && (!pickerSearch.trim() || w.label.toLowerCase().includes(pickerSearch.toLowerCase())));
                  if (!catWidgets.length) return null;
                  return (
                    <div key={catId} className="dashboard-picker-category">
                      <div className="dashboard-picker-cat-label">{cat.icon} {cat.label}</div>
                      {catWidgets.map(w => (
                        <button key={w.id} type="button" className="dashboard-picker-item" onClick={() => addWidget(w.id)}>
                          {w.type === 'sticky' && <span className="dashboard-picker-sticky-dot" style={{ background: w.color }}></span>}
                          {w.label}
                          {w.description && <span className="dashboard-picker-desc">{w.description}</span>}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
